import { useEffect, useState } from 'react'
import { Col, Row, Container } from 'reactstrap'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { isAuthenticated, logout } from 'utils/authService'
import { About } from './About'
import { RegistrationForm } from './RegistrationForm'
import { makeGetRequest, makePostRequest, loginRedirect } from 'utils/api'
import moment from 'moment-timezone'
import { useDispatch } from 'react-redux'
import { NotificationList } from 'components/common/NotificationList'
import { formatResponse, formatErrorMessage } from 'utils/formatting'
import { useNavigate } from 'react-router-dom'
import { PageTitle } from 'components/common/PageTitle'

const requestProgress = {
  signup: 0
}

const OrganizationRegistration = () => {
  const reduxDispatch = useDispatch()
  const [step, setStep] = useState(1)
  const [verified, setVerified] = useState(false)
  const [emailExists, setEmailExists] = useState(false)
  const [percent, setPercent] = useState(0)
  const [remainingPercent, setRemainingPercent] = useState(100)
  const [token, setToken] = useState('')
  const [timezones, setTimezones] = useState([])
  const navigate = useNavigate()

  useEffect(() => {
    const init = async () => {
      if (isAuthenticated()) {
        loginRedirect(navigate)
      } else {
        await logout()
      }
    }
    init()
  }, [navigate])

  useEffect(() => {
    if (percent >= 100) {
      loginRedirect(navigate, token)
    }
  }, [percent, token, navigate])

  useEffect(() => {
    let interval
    // add delay so dns can propagate
    if (percent > 0 && percent <= 100) {
      interval = setInterval(() => {
        setPercent(percent => percent + remainingPercent / 60)
      }, 1000)
    }

    return () => {
      clearInterval(interval)
    }
  }, [percent, remainingPercent])

  useEffect(() => {
    ;(async () => {
      const { timezones } = await makeGetRequest('/v1/timezones')
      setTimezones(
        timezones.map(r => ({
          value: r,
          label: `${r} (current ${moment().tz(r)})`
        }))
      )
    })()
  }, [])

  const uploadProgress = (prop, progressEvent) => {
    // save the individual file's progress percentage in object
    requestProgress[prop] = (progressEvent.loaded * 100) / (1.5 * progressEvent.total)
    // sum up all file progress percentages to calculate the overall progress
    const totalPercent = requestProgress
      ? Object.values(requestProgress).reduce((sum, num) => sum + num, 0)
      : 0
    // divide the total percentage by the number of files
    const percent = parseInt(Math.round(totalPercent / Object.values(requestProgress).length))
    setPercent(percent)
    setRemainingPercent(100 - percent)
  }

  const formSubmit = async (values, { onError } = {}) => {
    setStep(3)
    try {
      const token = await makePostRequest(
        '/v1/tenants/register',
        {
          email: values.companyEmail,
          firstName: values.firstName,
          lastName: values.lastName,
          organizationName: values.companyName,
          organizationType: values.companyType.value,
          organizationInterest: values.companyInterest.value,
          password: values.password,
          roles: ['ROLE_ADMIN', 'ROLE_USER'],
          phone: values.phone,
          timezone: values.timezone.value,
          turnstileToken: values.turnstileToken || undefined
        },
        {
          onUploadProgress: function (progressEvent) {
            uploadProgress('signup', progressEvent)
          }
        }
      )
      setToken(token)
    } catch (error) {
      setStep(2)
      onError?.()
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was an issue. Please contact support',
          message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
          level: 'error'
        }
      })
    }
  }
  const verifyEmail = async email => {
    const url = '/v1/tenants/email'
    const masterSchemaHeader = {
      'x-tenant-schema': 'master'
    }
    try {
      const emailExists = await makeGetRequest(url, {
        params: {
          email
        },
        headers: masterSchemaHeader
      })
      if (emailExists) {
        setEmailExists(true)
        setVerified(false)
      } else {
        setVerified(true)
      }
    } catch (error) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was an issue. Please contact support',
          message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <PageTitle
        detail="Sign Up or Register for Your iDeal Construction CRM Account"
        description="Complete registration for iDeal Construction CRM and start managing your opportunities, bids, jobs, projects, and proposals. Expedite and automate follow-up."
      />
      <Container>
        <Row>
          <Col lg="5">
            <RegistrationForm
              step={step}
              verified={verified}
              setVerified={setVerified}
              setStep={setStep}
              formSubmit={formSubmit}
              verifyEmail={verifyEmail}
              emailExists={emailExists}
              setEmailExists={setEmailExists}
              percent={percent}
              timezones={timezones}
            />
          </Col>
          <Col lg="7">
            <About />
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default OrganizationRegistration
